import React, { useState } from 'react';
import '../../../component-styles/LibraryModal.css';
import API_BASE from '../../../utils/api.js';
import { validateWebImageFile } from '../../../utils/imageUpload.js';
import notify from '../../../utils/notify';
import productDefaultImg from '../../../assets/product-default.png';

const DEFAULT_IMAGE = productDefaultImg;

export default function AddItemForm({ sectionId, item, onClose, onSave }) {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');
  const isEdit = Boolean(item);

  const [form, setForm] = useState({
    name: item?.name || '',
    description: item?.description || '',
    pricePerUnit: item?.pricePerUnit ?? item?.tradePrice ?? '',
    imageUrl: item?.imageUrl || '',
    vendor: item?.vendor || '',
    sku: item?.sku || '',
    productUrl: item?.productUrl || '',
  });
  const [uploading, setUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    await uploadImageFile(file);
  }

  async function uploadImageFile(file) {
    if (!file) return;

    const validation = validateWebImageFile(file);
    if (!validation.ok) {
      notify(validation.error);
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('idToken');

      const res = await fetch(
        `${apiUrl}/api/upload/presigned-url?filename=${encodeURIComponent(
          file.name
        )}&type=${encodeURIComponent(validation.mime)}&size=${encodeURIComponent(file.size)}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (!res.ok) throw new Error('Failed to get signed URL');
      const { uploadUrl, fileUrl } = await res.json();

      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': validation.mime },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');

      setForm((prev) => ({ ...prev, imageUrl: fileUrl }));
    } catch (err) {
      console.error('Upload error:', err);
      notify('Failed to upload image.');
    } finally {
      setUploading(false);
    }
  }

  async function handleDrop(e) {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const payload = {
      ...form,
      sectionId: item?.sectionId || sectionId,
      userId,
      pricePerUnit: Number(form.pricePerUnit) || 0,
    };

    try {
      let res;
      if (isEdit) {
        const itemId = item.sk || item.itemId;
        res = await fetch(`${apiUrl}/api/library/${userId}/items/${encodeURIComponent(itemId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${apiUrl}/api/library/${userId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) throw new Error('Failed to save item');
      const saved = await res.json();
      onSave?.(saved);
      onClose?.();
    } catch (err) {
      console.error(err);
      notify('Error saving item.');
    }
  }

  return (
    <div className="library-modal-overlay">
      <div className="library-modal-content">
        <h3 className="library-modal-title">{isEdit ? 'Edit Item' : 'Add Item'}</h3>
        <form onSubmit={handleSubmit} className="library-modal-form">
          <input
            name="name"
            placeholder="Item Name"
            value={form.name}
            onChange={handleChange}
            required
          />
          <textarea
            name="description"
            placeholder="Description"
            value={form.description}
            onChange={handleChange}
          />
          <input
            name="pricePerUnit"
            placeholder="Price per unit"
            type="number"
            value={form.pricePerUnit}
            onChange={handleChange}
          />
          <input
            name="vendor"
            placeholder="Vendor / Supplier (optional)"
            value={form.vendor}
            onChange={handleChange}
          />
          <input
            name="sku"
            placeholder="SKU / Part number (optional)"
            value={form.sku}
            onChange={handleChange}
          />
          <input
            name="productUrl"
            placeholder="Product link URL (optional)"
            type="url"
            value={form.productUrl}
            onChange={handleChange}
          />

          {/* Upload box */}
          <div
            className="upload-box"
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragActive(false);
            }}
            onDrop={handleDrop}
            style={isDragActive ? { outline: '2px dashed #7ca65c', outlineOffset: 4 } : undefined}
          >
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.svg,image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml"
              onChange={handleFileChange}
            />
            <input
              type="text"
              placeholder="or paste image URL"
              value={form.imageUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, imageUrl: e.target.value }))}
            />
          </div>

          {/* Preview */}
          <div className="upload-preview">
            <img
              src={form.imageUrl || DEFAULT_IMAGE}
              alt="Preview"
              style={{ maxWidth: '100%', borderRadius: '6px' }}
            />
          </div>

          <div className="library-modal-actions">
            <button type="button" className="library-modal-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="library-modal-save-btn" disabled={uploading}>
              {uploading ? 'Uploading...' : isEdit ? 'Save Changes' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
